import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Markdown } from "@/components/Markdown";
import { AddEvidenceDialog } from "@/components/AddEvidenceDialog";
import { PriorityBadge, GateBadge, SegmentBadge, EvidenceBadge, ScoreBar, fmtDate, VerifiedTriggerBadge } from "@/lib/radar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  ArrowLeft, BadgeCheck, ShieldQuestion, Sparkles, RefreshCw, PencilLine, MessageSquarePlus,
  Radar as RadarIcon, ExternalLink, FileSearch, History, ClipboardCheck, AlertTriangle, RotateCcw,
} from "lucide-react";

const ANZSIC_INDUSTRIES = [
  "Agriculture, Forestry and Fishing",
  "Mining",
  "Manufacturing",
  "Electricity, Gas, Water and Waste Services",
  "Construction",
  "Wholesale Trade",
  "Retail Trade",
  "Accommodation and Food Services",
  "Transport, Postal and Warehousing",
  "Information Media and Telecommunications",
  "Financial and Insurance Services",
  "Rental, Hiring and Real Estate Services",
  "Professional, Scientific and Technical Services",
  "Administrative and Support Services",
  "Public Administration and Safety",
  "Education and Training",
  "Health Care and Social Assistance",
  "Arts and Recreation Services",
  "Other Services",
];

const OVERRIDE_FIELDS: Record<string, { label: string; type: string; options?: string[] }> = {
  employee_estimate: { label: "Employee estimate", type: "number" },
  sector_fit: { label: "Sector fit", type: "select", options: ["high_value", "adjacent", "none"] },
  industry: { label: "Industry / sector", type: "select", options: ANZSIC_INDUSTRIES },
  disp_status: { label: "DISP evidence status", type: "select", options: ["Verified current", "Organisation-stated", "Publicly claimed", "No Evidence Identified", "Not assessed", "Not applicable"] },
  essential_eight_status: { label: "Essential Eight status", type: "select", options: ["Not assessed", "Self-assessed", "Independent assessment", "Defence assessment", "Assessment in progress", "No Evidence Identified"] },
  security_opportunity_status: { label: "Security opportunity", type: "select", options: ["Prospect-confirmed", "Evidence-backed", "Discovery hypothesis", "No Evidence Identified", "Research Required"] },
  entity_confirmed: { label: "Entity confirmed", type: "bool" },
  human_review_approved: { label: "Human review approved", type: "bool" },
  suppressed: { label: "Suppression (Do Not Contact)", type: "bool" },
  action_gate: { label: "Action gate (override)", type: "select", options: ["Review Ready", "Validate Data", "Research Required", "Research", "Nurture", "Established Account Pathway", "Do Not Contact"] },
  priority: { label: "Priority (override)", type: "select", options: ["P1", "P2", "P3", "P4", "Exclude"] },
  known_prime_status: { label: "Entrenchment: Known prime status", type: "bool" },
  over_500_employees: { label: "Entrenchment: Over 500 employees", type: "bool" },
  tier1_status: { label: "Entrenchment: Tier-1 status", type: "bool" },
  confirmed_disp_membership: { label: "Entrenchment: Confirmed DISP membership", type: "bool" },
  mature_security_function: { label: "Entrenchment: Mature security function", type: "bool" },
  long_defence_history: { label: "Entrenchment: Long Defence history", type: "bool" },
  major_programmes: { label: "Entrenchment: Major programmes", type: "bool" },
  long_term_prime_relationships: { label: "Entrenchment: Long-term prime relationships", type: "bool" },
};

const LEAD_STATES = ["New", "Reviewed", "Qualified", "Contacted", "Engaged", "Opportunity", "Client", "Nurture", "Disqualified"];
const FEEDBACK_RATINGS = ["Excellent Lead", "Potential Lead", "Poor Lead", "Established Defence", "Incorrect Data"];
const AI_KINDS = [
  { k: "review_brief", label: "Draft review brief" },
  { k: "discovery_hypotheses", label: "Discovery hypotheses" },
  { k: "missing_evidence", label: "Identify missing evidence" },
  { k: "pathway", label: "Recommend pathway" },
  { k: "summary", label: "Summarise evidence" },
];

const AUDIT_LABELS: Record<string, string> = {
  score_run: "Score run",
  manual_override: "Manual override",
  manual_override_reset: "Override reset",
  ai_analysis: "AI analysis",
  lead_activity: "Lead activity",
  feedback: "Feedback recorded",
  export: "Export",
  company_added: "Company added to Radar",
  discovery_call_captured: "Discovery call captured",
  discovery_batch_captured: "Evidence added",
  company_details_edited: "Company details edited",
};

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function summariseAudit(a: any): string {
  const d = a.detail || {};
  switch (a.event_type) {
    case "score_run": return d.opportunity_score != null ? `Opportunity ${d.opportunity_score}` : "Re-scored";
    case "manual_override": return `${d.field}: ${String(d.old_value)} → ${String(d.new_value)}`;
    case "manual_override_reset": return `${d.field} reset to ${String(d.reverted_to)}`;
    case "ai_analysis": return `${(d.kind || "").replace(/_/g, " ")} · ${d.status || ""}`;
    case "lead_activity": {
      const parts: string[] = [];
      if (d.lead_status) parts.push(`→ ${d.lead_status}`);
      if (d.note) parts.push(String(d.note));
      return parts.length ? parts.join(" · ") : "Activity";
    }
    case "feedback": return d.rating || "Feedback";
    case "export": return "Intelligence exported (CSV)";
    case "discovery_call_captured": {
      const st = d.signal_type && d.signal_type !== "context_only" ? String(d.signal_type).replace(/_/g, " ") : "context only – no score";
      return `${st}${d.evidence_class ? " · " + d.evidence_class : ""}`;
    }
    case "discovery_batch_captured": {
      const n = d.count ?? (Array.isArray(d.items) ? d.items.length : 0);
      return `${n} evidence item${n === 1 ? "" : "s"} captured`;
    }
    case "company_details_edited": {
      const ch = Array.isArray(d.changes) ? d.changes : [];
      if (ch.length === 0) return "Details edited";
      if (ch.length <= 3) return ch.map((x: any) => String(x.field).replace(/_/g, " ")).join(", ") + " updated";
      return `${ch.length} fields updated`;
    }
    default: return "";
  }
}

const SECTOR_FIT_LABEL: Record<string, string> = {
  high_value: "High value",
  adjacent: "Adjacent",
  none: "None",
};
const SIGNAL_FAMILY_LABEL: Record<string, string> = {
  security: "Security & readiness",
  defence_intent: "Defence Intent",
  why_now: "Why Now",
};

function OverrideTag({ onClick }: { onClick?: () => void }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" data-testid="override-tag" onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            className="inline-flex items-center gap-0.5 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors cursor-pointer">
            <PencilLine className="h-2.5 w-2.5" /> Override
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-slate-900 text-slate-50 leading-relaxed">
          Click to override this field — opens Verify / Override pre-set to this field. A manual override takes precedence over the derived value and is recorded in the audit trail.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function StatRow({ label, value, overrideField, onOverride }: { label: string; value: any; overrideField?: string; onOverride?: (f: string) => void }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500 flex items-center gap-1.5">{label}{overrideField && onOverride && <OverrideTag onClick={() => onOverride(overrideField)} />}</span>
      <span className="text-sm text-slate-800 num">{value ?? "—"}</span>
    </div>
  );
}

function Component({ name, total, cap }: { name: string; total: number; cap: number }) {
  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{name}</span>
        <span className="num text-slate-800 font-medium">{total} <span className="text-slate-400">/ {cap}</span></span>
      </div>
      <div className="h-1.5 mt-1 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-teal-600 rounded-full" style={{ width: `${(total / cap) * 100}%` }} />
      </div>
    </div>
  );
}

function EvidenceCard({ e, onOpen }: { e: any; onOpen: (e: any) => void }) {
  return (
    <button onClick={() => onOpen(e)} data-testid={`evidence-${e.id}`}
      className="w-full text-left rounded-md border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800 truncate">{e.signal_type_label || e.source_title}</span>
        <EvidenceBadge value={e.evidence_class} />
      </div>
      {e.signal_type_label && <div className="text-[11px] text-slate-500 mt-0.5 truncate">Source: {e.source_title}</div>}
      <div className="text-xs text-slate-500 mt-1 line-clamp-2">{e.evidence_text}</div>
      <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
        <span>{e.source_type}</span>·<span className="num">{fmtDate(e.event_date)}</span>
      </div>
    </button>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState<any>(null);
  const [activeEvidence, setActiveEvidence] = useState<any>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [vField, setVField] = useState("employee_estimate");
  const [vValue, setVValue] = useState("");
  const [vReason, setVReason] = useState("");
  const [actOpen, setActOpen] = useState(false);
  const [actStatus, setActStatus] = useState("");
  const [actNote, setActNote] = useState("");
  const [fbOpen, setFbOpen] = useState(false);
  const [fbRating, setFbRating] = useState("");
  const [fbNote, setFbNote] = useState("");
  const [aiKind, setAiKind] = useState("review_brief");
  const [aiLoading, setAiLoading] = useState(false);
  const [dcOpen, setDcOpen] = useState(false);
  const [catalogue, setCatalogue] = useState<any>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState<any>({});

  const canEdit = user && ["admin", "analyst"].includes(user.role);
  const canWork = user && ["admin", "analyst", "sales"].includes(user.role);

  const load = () => api.get(`/companies/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!d) return <div className="text-slate-400 text-sm">Loading company…</div>;
  const c = d.company, s = d.score, comp = s?.components || {};

  const rescore = async () => {
    await api.post(`/companies/${id}/score`);
    toast.success("Score recomputed");
    load();
  };

  const openOverride = (field: string) => {
    setVField(field); setVValue(""); setVReason(""); setVerifyOpen(true);
  };

  const submitVerify = async () => {
    if (!vReason.trim()) { toast.error("A reason is required for every override"); return; }
    const meta = OVERRIDE_FIELDS[vField];
    let val: any = vValue;
    if (meta.type === "bool") val = vValue === "true";
    if (meta.type === "number") val = Number(vValue);
    try {
      await api.post(`/companies/${id}/verify`, { field: vField, new_value: val, reason: vReason });
      toast.success("Override recorded and re-scored");
      setVerifyOpen(false); setVReason(""); setVValue("");
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitActivity = async () => {
    try {
      await api.post(`/companies/${id}/activities`, { lead_status: actStatus || undefined, note: actNote || undefined });
      toast.success("Activity logged");
      setActOpen(false); setActNote(""); setActStatus("");
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitFeedback = async () => {
    if (!fbRating) { toast.error("Select a rating"); return; }
    try {
      await api.post(`/companies/${id}/feedback`, { rating: fbRating, note: fbNote || undefined });
      toast.success("Feedback recorded");
      setFbOpen(false); setFbNote(""); setFbRating("");
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetOverride = async (field: string) => {
    const reason = window.prompt(`Reason for resetting "${field}" to its collected value?`);
    if (!reason || !reason.trim()) { toast.error("A reason is required to reset"); return; }
    try {
      await api.post(`/companies/${id}/verify/reset`, { field, reason });
      toast.success(`Override on "${field}" reset and re-scored`);
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDiscovery = async () => {
    if (!catalogue) {
      try { const { data } = await api.get("/evidence/catalogue"); setCatalogue(data); }
      catch (e: any) { toast.error(apiError(e.response?.data?.detail)); return; }
    }
    setDcOpen(true);
  };

  const openEdit = () => {
    setEdit({
      company_name: c.company_name || "",
      legal_name: c.legal_name || "",
      abn: c.abn || "",
      acn: c.acn || "",
      website: c.website || "",
      city: c.city || "",
      state: c.state || "",
      primary_discovery_source: c.primary_discovery_source || "",
      icn_reference_url: c.icn_reference_url || "",
      description: c.description || "",
      duplicate_review_state: c.duplicate_review_state || "unique",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!edit.company_name?.trim()) { toast.error("Company name is required"); return; }
    try {
      const { data } = await api.patch(`/companies/${id}/details`, edit);
      toast.success(data.changes ? `${data.changes} field${data.changes === 1 ? "" : "s"} updated` : "No changes to save");
      setEditOpen(false);
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const genAI = async () => {
    setAiLoading(true);
    try {
      await api.post(`/companies/${id}/ai-analysis`, { kind: aiKind });
      toast.success("AI analysis generated (stored separately, non-scoring)");
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const meta = OVERRIDE_FIELDS[vField];

  return (
    <div className="space-y-4" data-testid="company-detail">
      <button onClick={() => navigate("/companies")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Company List
      </button>

      {/* Header */}
      <Card className="p-4 border-slate-200 shadow-none">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold text-slate-900">{c.company_name}</h1>
              {c.manual_verification?.verified && <BadgeCheck className="h-5 w-5 text-emerald-600" />}
              {s?.p0_trigger && <VerifiedTriggerBadge testid="detail-trigger" />}
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              {c.legal_name} · {c.industry || "Sector TBC"} · {c.city ? `${c.city}, ` : ""}{c.state || "—"} · ABN {c.abn || "—"}
            </div>
            {c.manual_verification?.verified && (
              <div className="text-[11px] text-emerald-700 mt-1">Verified by {c.manual_verification.verified_by} — {fmtDate(c.manual_verification.verified_at)}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {s && <PriorityBadge value={s.priority} testid="detail-priority" />}
            {s && <GateBadge value={s.action_gate} testid="detail-gate" />}
            {s && <SegmentBadge value={s.entrenchment_segment} testid="detail-segment" />}
            {canEdit && <Button size="sm" variant="outline" onClick={rescore} data-testid="rescore-btn"><RefreshCw className="h-4 w-4 mr-1" /> Re-score</Button>}
            {canWork && (
              <Button size="sm" variant="outline" onClick={openDiscovery} data-testid="discovery-call-btn">
                <MessageSquarePlus className="h-4 w-4 mr-1" /> Add Evidence
              </Button>
            )}
            {canEdit && (
              <Button size="sm" className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={() => setVerifyOpen(true)} data-testid="verify-btn">
                <PencilLine className="h-4 w-4 mr-1" /> Verify / Override
              </Button>
            )}
          </div>
        </div>
        {/* Score summary */}
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Opportunity Score</div>
              <div className="mt-1"><ScoreBar value={s.opportunity_score} testid="opp-score" /></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Evidence Confidence</div>
              <div className="mt-1"><ScoreBar value={s.evidence_confidence} kind="confidence" testid="conf-score" /></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Entrenchment</div>
              <div className="mt-1"><ScoreBar value={s.entrenchment_score} kind="entrenchment" testid="entrench-score" /></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">P1 Qualified</div>
              <div className={`text-sm font-semibold mt-1 ${s.p1_qualified ? "text-emerald-700" : "text-slate-400"}`}>{s.p1_qualified ? "Yes — review ready" : "No — gate not met"}</div>
            </div>
          </div>
        )}
      </Card>

      {/* Contextual guardrail banner */}
      {s && (() => {
        const gates = s.gates || {};
        let banner: { cls: string; icon: any; title: string; body: string } | null = null;
        if (gates.suppressed || s.action_gate === "Do Not Contact") {
          banner = { cls: "bg-red-50 border-red-300 text-red-800", icon: AlertTriangle,
            title: "Do Not Contact — active suppression",
            body: "This record is suppressed. No outreach is permitted and it is excluded from contact workflows." };
        } else if (s.p1_qualified) {
          banner = { cls: "bg-emerald-50 border-emerald-300 text-emerald-800", icon: ShieldQuestion,
            title: "P1 Immediate Review — all gates satisfied",
            body: "Opportunity ≥ 80, Evidence Confidence ≥ 70, a current attributable signal, confirmed entity and human review approval are all met. Review Ready." };
        } else if (s.priority === "P1") {
          banner = { cls: "bg-amber-50 border-amber-300 text-amber-900", icon: AlertTriangle,
            title: "P1 opportunity score — review gate not yet met",
            body: "The opportunity score reached the P1 band, but confidence, a current signal, confirmed entity or human review approval is outstanding. Validate data before any review." };
        } else if (["Established Defence", "Prime/Mature"].includes(s.entrenchment_segment)) {
          banner = { cls: "bg-slate-100 border-slate-300 text-slate-700", icon: ShieldQuestion,
            title: `${s.entrenchment_segment} — Established Account Pathway`,
            body: "Routed to a human-led established-account review. Entrenchment is a separate segment and does not reduce the Opportunity Score." };
        } else if (["Research Required", "Research"].includes(s.action_gate)) {
          banner = { cls: "bg-slate-50 border-slate-300 text-slate-700", icon: ShieldQuestion,
            title: "Research state",
            body: "More attributable evidence is required before this candidate can progress. Absence of evidence is a research state, not a gap." };
        }
        if (!banner) return null;
        const Icon = banner.icon;
        return (
          <div data-testid="guardrail-banner" className={`flex items-start gap-2 rounded-md border p-3 text-sm ${banner.cls}`}>
            <Icon className="h-4 w-4 mt-0.5 shrink-0" />
            <div><span className="font-semibold">{banner.title}.</span> <span className="opacity-90">{banner.body}</span></div>
          </div>
        );
      })()}

      <Tabs defaultValue="overview">
        <TabsList data-testid="detail-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="scoring">Scoring Breakdown</TabsTrigger>
          <TabsTrigger value="ai">AI Insights</TabsTrigger>
          <TabsTrigger value="sales">Sales & Feedback</TabsTrigger>
          <TabsTrigger value="audit">Verification & Audit</TabsTrigger>
        </TabsList>

        {/* OVERVIEW: split view, verified facts vs discovery signals */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Left 65% : verified facts + qualification evidence */}
            <div className="lg:col-span-8 space-y-4">
              <Card className="p-4 border-slate-200 shadow-none" data-testid="company-facts">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-emerald-600" /> Verified Facts & Identity</h3>
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-details-btn">
                      <PencilLine className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <div>
                    <StatRow label="Legal name" value={c.legal_name} />
                    <StatRow label="ABN" value={c.abn} />
                    <StatRow label="ACN" value={c.acn} />
                    <StatRow label="Website" value={c.website} />
                    <StatRow label="Employees (est.)" value={c.employee_estimate?.value} overrideField="employee_estimate" onOverride={canEdit ? openOverride : undefined} />
                    <StatRow label="Employee source" value={c.employee_estimate?.source} />
                  </div>
                  <div>
                    <StatRow label="Industry" value={c.industry} overrideField="industry" onOverride={canEdit ? openOverride : undefined} />
                    <StatRow label="Sector fit" value={SECTOR_FIT_LABEL[c.sector_fit] || c.sector_fit} overrideField="sector_fit" onOverride={canEdit ? openOverride : undefined} />
                    <StatRow label="Location" value={[c.city, c.state].filter(Boolean).join(", ") || null} />
                    <StatRow label="Primary discovery source" value={c.primary_discovery_source} />
                    <StatRow label="Duplicate review" value={c.duplicate_review_state} />
                    <StatRow label="ICN reference" value={c.icn_reference_url ? "Recorded (manual)" : "—"} />
                  </div>
                </div>
                {c.description && <p className="text-sm text-slate-600 mt-3">{c.description}</p>}
              </Card>

              <Card className="p-4 border-slate-200 shadow-none" data-testid="security-readiness">
                <h3 className="font-heading font-semibold text-slate-800 mb-3">Security & Readiness Intelligence</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">DISP evidence status {canEdit && <OverrideTag onClick={() => openOverride("disp_status")} />}</div>
                    <div className="mt-1"><EvidenceBadge value={c.security?.disp_status} /></div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">Essential Eight status {canEdit && <OverrideTag onClick={() => openOverride("essential_eight_status")} />}</div>
                    <div className="mt-1 text-sm text-slate-700">{c.security?.essential_eight_status}</div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">Security opportunity {canEdit && <OverrideTag onClick={() => openOverride("security_opportunity_status")} />}</div>
                    <div className="mt-1"><EvidenceBadge value={c.security?.security_opportunity_status} /></div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">FOCI / due diligence</div>
                    <div className="mt-1 text-sm text-slate-700">{c.security?.foci_status}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 mt-3">
                  <ShieldQuestion className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Absence of public DISP / Essential Eight / ISO 27001 evidence is a research state — never proof of a gap, non-membership or compliance failure.
                </div>
              </Card>

              {/* Qualification Evidence — SOLID, verified styling */}
              <Card className="p-4 border-slate-200 shadow-none" data-testid="qualification-evidence">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-emerald-600" /> Qualification Evidence</h3>
                  <span className="text-[11px] text-slate-400">Why this company may warrant DefencIQ review</span>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {d.qualification_evidence.length === 0 && <div className="text-sm text-slate-400">No qualification evidence recorded.</div>}
                  {d.qualification_evidence.map((e: any) => <EvidenceCard key={e.id} e={e} onOpen={setActiveEvidence} />)}
                </div>
              </Card>
            </div>

            {/* Right 35% : Discovery signals (DASHED, distinct background) */}
            <div className="lg:col-span-4 space-y-4">
              <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-slate-50 p-4" data-testid="discovery-signals">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-heading font-semibold text-indigo-900 flex items-center gap-2"><RadarIcon className="h-4 w-4" /> Discovery Signals</h3>
                </div>
                <p className="text-[11px] text-indigo-700/70 mb-3">Why this company is on our radar now — kept distinct from verified qualification evidence.</p>
                <div className="space-y-2">
                  {d.discovery_signals.length === 0 && <div className="text-sm text-slate-400">No discovery signals.</div>}
                  {d.discovery_signals.map((e: any) => <EvidenceCard key={e.id} e={e} onOpen={setActiveEvidence} />)}
                </div>
              </div>

              <Card className="p-4 border-slate-200 shadow-none" data-testid="signal-timeline">
                <h3 className="font-heading font-semibold text-slate-800 mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Signal Timeline</h3>
                <div className="space-y-3 relative pl-4 border-l border-slate-200">
                  {d.signal_timeline.map((sig: any) => (
                    <div key={sig.id} className="relative">
                      <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-teal-600 border-2 border-white" />
                      <div className="text-sm text-slate-800">{sig.signal_type_label || sig.title}</div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-400"><span className="num">{fmtDate(sig.event_date)}</span> · {SIGNAL_FAMILY_LABEL[sig.signal_family] || sig.signal_family}</div>
                    </div>
                  ))}
                  {d.signal_timeline.length === 0 && <div className="text-sm text-slate-400">No signals.</div>}
                </div>
              </Card>

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="open-workspace"><FileSearch className="h-4 w-4 mr-2" /> Open Evidence Workspace</Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-lg overflow-auto" data-testid="evidence-workspace">
                  <SheetHeader>
                    <SheetTitle>Evidence Workspace</SheetTitle>
                    <SheetDescription>Logical evidence view for {c.company_name}. Not a sensitive-document repository.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-4 space-y-2">
                    {d.evidence.map((e: any) => (
                      <div key={e.id} className="rounded-md border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-slate-800">{e.signal_type_label || e.source_title}</span>
                          <EvidenceBadge value={e.evidence_class} />
                        </div>
                        {e.signal_type_label && <div className="text-[11px] text-slate-500 mt-0.5">Source: {e.source_title}</div>}
                        <div className="text-xs text-slate-500 mt-1">{e.evidence_text}</div>
                        <div className="grid grid-cols-2 gap-x-4 mt-2 text-[11px] text-slate-500">
                          <span>Category: {e.evidence_category}</span>
                          <span>Review: {e.review_status}</span>
                          <span>Confidence: <span className="num">{e.confidence_score}</span></span>
                          <span>Checked: <span className="num">{fmtDate(e.last_checked_date)}</span></span>
                          <span>Permitted use: {e.allowed_onward_use}</span>
                          <span>Owner: {e.connector_owner}</span>
                        </div>
                        {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-teal-700 flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" /> Source</a>}
                      </div>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </TabsContent>

        {/* SCORING */}
        <TabsContent value="scoring" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4 border-slate-200 shadow-none" data-testid="scoring-breakdown">
              <h3 className="font-heading font-semibold text-slate-800 mb-2">Radar Opportunity Score — <span className="num">{s?.opportunity_score}</span>/100</h3>
              <Component name="SME Fit" total={comp.sme_fit?.total || 0} cap={comp.sme_fit?.cap || 20} />
              <Component name="Defence Intent" total={comp.defence_intent?.total || 0} cap={comp.defence_intent?.cap || 30} />
              <Component name="Security Opportunity" total={comp.security_opportunity?.total || 0} cap={comp.security_opportunity?.cap || 30} />
              <Component name="Why Now (recency-adjusted)" total={comp.why_now?.total || 0} cap={comp.why_now?.cap || 20} />
              {comp.correlation_adjustment > 0 && (
                <div className="flex items-center justify-between text-sm mt-2 text-amber-700">
                  <span className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Correlation cap adjustment</span>
                  <span className="num font-medium">−{comp.correlation_adjustment}</span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Evidence Confidence breakdown</div>
                <div className="grid grid-cols-2 gap-1 text-sm text-slate-600">
                  <span>Source quality</span><span className="num text-right">{comp.evidence_confidence_breakdown?.source_quality}/35</span>
                  <span>Recency</span><span className="num text-right">{comp.evidence_confidence_breakdown?.recency}/25</span>
                  <span>Corroboration</span><span className="num text-right">{comp.evidence_confidence_breakdown?.corroboration}/20</span>
                  <span>Verification state</span><span className="num text-right">{comp.evidence_confidence_breakdown?.verification}/20</span>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-slate-200 shadow-none" data-testid="reason-codes">
              <h3 className="font-heading font-semibold text-slate-800 mb-2">Score Explanation (reason codes)</h3>
              <div className="text-[11px] text-slate-400 mb-2">Model {s?.model_version} · deterministic · every contribution is traceable to evidence.</div>
              <ul className="space-y-1.5 max-h-[420px] overflow-auto">
                {(s?.reason_codes || []).map((r: string, i: number) => (
                  <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-teal-600">•</span>{r}</li>
                ))}
              </ul>
            </Card>
          </div>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <Card className="p-4 border-slate-200 shadow-none" data-testid="ai-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-600" /> AI Discovery Assistant</h3>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Select value={aiKind} onValueChange={setAiKind}>
                    <SelectTrigger className="w-[220px]" data-testid="ai-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{AI_KINDS.map((a) => <SelectItem key={a.k} value={a.k}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={genAI} disabled={aiLoading} data-testid="ai-generate" className="bg-indigo-600 hover:bg-indigo-700">
                    {aiLoading ? "Generating…" : "Generate"}
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-start gap-2 text-[11px] text-indigo-800 bg-indigo-50 border border-dashed border-indigo-200 rounded p-2 mt-3">
              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              AI output is an Analyst / AI Discovery Hypothesis. It never sets verified facts, never alters scores and is stored separately for analyst review.
            </div>
            <div className="mt-3 space-y-3">
              {d.ai_analyses.length === 0 && <div className="text-sm text-slate-400">No AI analyses yet.</div>}
              {d.ai_analyses.map((a: any) => (
                <div key={a.id} className="rounded-md border border-dashed border-indigo-200 bg-indigo-50/40 p-3" data-testid={`ai-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{a.kind.replace(/_/g, " ")}</span>
                    <span className="text-[11px] text-slate-400 num">{fmtDate(a.created_at)}</span>
                  </div>
                  <div className="mt-1"><Markdown>{a.output_text}</Markdown></div>
                  <div className="text-[11px] text-slate-400 mt-2">Model: {a.model_name} · {a.analyst_review_status} · Citations: {(a.citations || []).length}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* SALES & FEEDBACK */}
        <TabsContent value="sales" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4 border-slate-200 shadow-none" data-testid="sales-activity">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-slate-800">Lead Workflow & Activity</h3>
                {canWork && <Button size="sm" variant="outline" onClick={() => setActOpen(true)} data-testid="add-activity-btn"><MessageSquarePlus className="h-4 w-4 mr-1" /> Log activity</Button>}
              </div>
              <div className="flex items-center gap-2 mb-3 text-sm">
                <span className="text-slate-500">Current status:</span>
                <span className="font-semibold text-slate-800">{c.sales?.lead_status}</span>
                {c.sales?.lead_owner && <span className="text-slate-400">· {c.sales.lead_owner}</span>}
              </div>
              <div className="space-y-2 max-h-[320px] overflow-auto">
                {d.lead_activities.map((a: any) => (
                  <div key={a.id} className="border-b border-slate-100 pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">{a.lead_status ? `→ ${a.lead_status}` : "Note"}</span>
                      <span className="text-[11px] text-slate-400 num">{fmtDate(a.created_at)}</span>
                    </div>
                    {a.note && <div className="text-xs text-slate-500">{a.note}</div>}
                    <div className="text-[11px] text-slate-400">{a.user}</div>
                  </div>
                ))}
                {d.lead_activities.length === 0 && <div className="text-sm text-slate-400">No activity logged.</div>}
              </div>
            </Card>
            <Card className="p-4 border-slate-200 shadow-none" data-testid="feedback-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-slate-800">"Was Radar right?" Feedback</h3>
                {canWork && <Button size="sm" variant="outline" onClick={() => setFbOpen(true)} data-testid="add-feedback-btn">Add feedback</Button>}
              </div>
              <div className="space-y-2 max-h-[320px] overflow-auto">
                {d.feedback.map((f: any) => (
                  <div key={f.id} className="border-b border-slate-100 pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{f.rating}</span>
                      <span className="text-[11px] text-slate-400 num">{fmtDate(f.created_at)}</span>
                    </div>
                    {f.note && <div className="text-xs text-slate-500">{f.note}</div>}
                    <div className="text-[11px] text-slate-400">{f.user} · model {f.model_version}</div>
                  </div>
                ))}
                {d.feedback.length === 0 && <div className="text-sm text-slate-400">No feedback yet.</div>}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* AUDIT / VERIFICATION */}
        <TabsContent value="audit" className="mt-4">
          <Card className="p-4 border-slate-200 shadow-none" data-testid="overrides-panel">
            <h3 className="font-heading font-semibold text-slate-800 mb-2">Manual Verification Record</h3>
            {d.manual_overrides.length === 0 && <div className="text-sm text-slate-400">No manual overrides recorded.</div>}
            <div className="space-y-2">
              {d.manual_overrides.map((o: any) => (
                <div key={o.id} className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800">{o.field} {o.reset && <span className="text-[10px] uppercase tracking-wide text-slate-400 ml-1">(reset)</span>}</span>
                    <span className="text-[11px] text-slate-400 num">{fmtDate(o.created_at)}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    <span className="line-through text-slate-400">{String(o.old_value)}</span> → <span className="font-medium">{String(o.new_value)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Reason: {o.reason}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-slate-400">{o.user}</span>
                    {canEdit && !o.reset && (
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] text-teal-700 hover:text-teal-900"
                        onClick={() => resetOverride(o.field)} data-testid={`reset-${o.field}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Reset to collected
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {canEdit && d.audit_events && (
            <Card className="p-0 border-slate-200 shadow-none overflow-hidden mt-4" data-testid="company-audit-trail">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <History className="h-4 w-4 text-teal-700" />
                <h3 className="font-heading font-semibold text-slate-800">Company Audit Trail</h3>
                <span className="text-[11px] text-slate-400">append-only · score runs, AI, activity, overrides, exports</span>
              </div>
              <div className="max-h-[420px] overflow-auto divide-y divide-slate-100">
                {d.audit_events.length === 0 && <div className="px-4 py-6 text-sm text-slate-400">No audit events recorded for this company yet.</div>}
                {d.audit_events.map((a: any) => (
                  <div key={a.id} className="px-4 py-2.5" data-testid={`audit-event-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">{AUDIT_LABELS[a.event_type] || a.event_type}</span>
                      <span className="text-[11px] text-slate-400 num whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-500 truncate">{summariseAudit(a)}</span>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap">{a.user_email} ({a.role})</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Evidence detail dialog */}
      <Dialog open={!!activeEvidence} onOpenChange={(o) => !o && setActiveEvidence(null)}>
        <DialogContent data-testid="evidence-dialog">
          {activeEvidence && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{activeEvidence.signal_type_label || activeEvidence.source_title} <EvidenceBadge value={activeEvidence.evidence_class} /></DialogTitle>
                <DialogDescription>{activeEvidence.signal_type_label ? `Source: ${activeEvidence.source_title} · ` : ""}{activeEvidence.evidence_category} · {activeEvidence.source_type}</DialogDescription>
              </DialogHeader>
              <p className="text-sm text-slate-700">{activeEvidence.evidence_text}</p>
              <div className="grid grid-cols-2 gap-2 text-[12px] text-slate-500 mt-2">
                <span>Event date: <span className="num">{fmtDate(activeEvidence.event_date)}</span></span>
                <span>Captured: <span className="num">{fmtDate(activeEvidence.captured_date)}</span></span>
                <span>Confidence: <span className="num">{activeEvidence.confidence_score}</span></span>
                <span>Collection: {activeEvidence.collection_method}</span>
                <span>Permitted purpose: {activeEvidence.permitted_purpose}</span>
                <span>Onward use: {activeEvidence.allowed_onward_use}</span>
                <span>Dedup key: <span className="num">{activeEvidence.dedup_key || "—"}</span></span>
                <span>Model: {activeEvidence.scoring_model_version || "—"}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Evidence — multi-select matrix dialog */}
      <AddEvidenceDialog open={dcOpen} onOpenChange={setDcOpen} catalogue={catalogue} companyId={id!} onSaved={load} />

      {/* Edit Company Details dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="edit-details-dialog">
          <DialogHeader>
            <DialogTitle>Edit Company Details</DialogTitle>
            <DialogDescription>Update identity and context facts. Scoring-related fields (industry, sector fit, employees, security status) are edited via Verify / Override. Changes are recorded in the audit trail.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Company name</Label>
              <Input data-testid="edit-company_name" value={edit.company_name || ""} onChange={(e) => setEdit({ ...edit, company_name: e.target.value })} />
            </div>
            <div>
              <Label>Legal name</Label>
              <Input data-testid="edit-legal_name" value={edit.legal_name || ""} onChange={(e) => setEdit({ ...edit, legal_name: e.target.value })} />
            </div>
            <div>
              <Label>Website</Label>
              <Input data-testid="edit-website" value={edit.website || ""} onChange={(e) => setEdit({ ...edit, website: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label>ABN</Label>
              <Input data-testid="edit-abn" value={edit.abn || ""} onChange={(e) => setEdit({ ...edit, abn: e.target.value })} />
            </div>
            <div>
              <Label>ACN</Label>
              <Input data-testid="edit-acn" value={edit.acn || ""} onChange={(e) => setEdit({ ...edit, acn: e.target.value })} />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="edit-city" value={edit.city || ""} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
            </div>
            <div>
              <Label>State / Territory</Label>
              <Select value={edit.state || "__none__"} onValueChange={(v) => setEdit({ ...edit, state: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="edit-state"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Primary discovery source</Label>
              <Input data-testid="edit-primary_discovery_source" value={edit.primary_discovery_source || ""} onChange={(e) => setEdit({ ...edit, primary_discovery_source: e.target.value })} />
            </div>
            <div>
              <Label>Duplicate review</Label>
              <Select value={edit.duplicate_review_state || "unique"} onValueChange={(v) => setEdit({ ...edit, duplicate_review_state: v })}>
                <SelectTrigger data-testid="edit-duplicate_review_state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unique">unique</SelectItem>
                  <SelectItem value="pending">pending</SelectItem>
                  <SelectItem value="confirmed">confirmed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>ICN reference URL</Label>
              <Input data-testid="edit-icn_reference_url" value={edit.icn_reference_url || ""} onChange={(e) => setEdit({ ...edit, icn_reference_url: e.target.value })} placeholder="Manual reference only" />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Textarea data-testid="edit-description" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitEdit} data-testid="edit-details-submit">Save details</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify / Override dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent data-testid="verify-dialog">
          <DialogHeader>
            <DialogTitle>Verify Lead — manual override</DialogTitle>
            <DialogDescription>Overrides take precedence over collected data and require a reason. An append-only audit event is created.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Field</Label>
              <Select value={vField} onValueChange={(v) => { setVField(v); setVValue(""); }}>
                <SelectTrigger data-testid="override-field"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(OVERRIDE_FIELDS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>New value</Label>
              {meta.type === "select" ? (
                <Select value={vValue} onValueChange={setVValue}>
                  <SelectTrigger data-testid="override-value-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{meta.options!.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : meta.type === "bool" ? (
                <Select value={vValue} onValueChange={setVValue}>
                  <SelectTrigger data-testid="override-value-bool"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent><SelectItem value="true">True</SelectItem><SelectItem value="false">False</SelectItem></SelectContent>
                </Select>
              ) : (
                <Input data-testid="override-value" type={meta.type === "number" ? "number" : "text"} value={vValue} onChange={(e) => setVValue(e.target.value)} />
              )}
            </div>
            <div>
              <Label>Reason (required)</Label>
              <Textarea data-testid="override-reason" value={vReason} onChange={(e) => setVReason(e.target.value)} placeholder="Justification for this override…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitVerify} data-testid="override-submit">Save override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity dialog */}
      <Dialog open={actOpen} onOpenChange={setActOpen}>
        <DialogContent data-testid="activity-dialog">
          <DialogHeader><DialogTitle>Log lead activity</DialogTitle>
            <DialogDescription>Contact/outreach is manual in Phase 01. No automated messages are sent.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Move to workflow state</Label>
              <Select value={actStatus} onValueChange={setActStatus}>
                <SelectTrigger data-testid="activity-status"><SelectValue placeholder="Keep current" /></SelectTrigger>
                <SelectContent>{LEAD_STATES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea data-testid="activity-note" value={actNote} onChange={(e) => setActNote(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitActivity} data-testid="activity-submit">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback dialog */}
      <Dialog open={fbOpen} onOpenChange={setFbOpen}>
        <DialogContent data-testid="feedback-dialog">
          <DialogHeader><DialogTitle>Was Radar right?</DialogTitle>
            <DialogDescription>Feedback is stored with the active scoring model version and does not change production rules.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rating</Label>
              <Select value={fbRating} onValueChange={setFbRating}>
                <SelectTrigger data-testid="feedback-rating"><SelectValue placeholder="Select rating" /></SelectTrigger>
                <SelectContent>{FEEDBACK_RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea data-testid="feedback-note" value={fbNote} onChange={(e) => setFbNote(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFbOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitFeedback} data-testid="feedback-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
